import React, { useEffect, useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';

const Ul = styled('ul')(({ theme }) => ({
  padding: 0,
  margin: 0,
}));
const Li = styled('li')(({ theme }) => ({
  'listStyleType': 'none',
  'padding': '5px',
  'cursor': 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.action.focus,
  },
}));

const KeyMap = {
  BACKSPACE: 8,
  DELETE: 46,
  DOWN: 40,
  LEFT: 37,
  RIGHT: 39,
  ENTER: 13,
  TAB: 9,
  UP: 38,
  ESC: 27,
  SPACE: 32,
};

const defaultDelimiter = '@';

function isMention(element) {
  return (
    typeof element.getAttribute === 'function' &&
    element.hasAttribute('data-mention-id')
  );
}

function Dropdown({ editor, suggestions, onClose, initialQuery }) {
  const range = editor.selection.getRng();
  const { x, y } = range.getBoundingClientRect();
  const rtePosition = editor.getContentAreaContainer();
  const rect = rtePosition.getBoundingClientRect();
  const [activeIndex, setActiveIndex] = useState(0);
  const [query, setQuery] = useState(initialQuery || '');
  const filtered = useMemo(() => {
    return query
      ? suggestions.filter((s) =>
          s.label.toLowerCase().includes(query.toLowerCase()),
        )
      : suggestions;
  }, [query]);

  useEffect(() => {
    bindEvents();
    return () => {
      unbindEvents();
    };
  }, [activeIndex, filtered]);

  useEffect(() => {
    const onChange = (e) => {
      if (e.inputType === 'insertText') {
        setQuery((prev) => prev + e.data);
      }
      // check if trigger is removed then hide dropdown
      if (e.inputType === 'deleteContentBackward') {
        const range = editor.selection.getRng();
        const node = range.startContainer;
        const content = node.textContent;
        const cursorPosition = range.startOffset;
        const mentionIndex = content.lastIndexOf(
          defaultDelimiter,
          cursorPosition - 1,
        );
        if (mentionIndex === -1) {
          onClose();
        }
      }
    };
    editor.on('input', onChange);
    return () => editor.off('input', onChange);
  }, []);

  function keyup(e) {
    switch (e.keyCode) {
      case KeyMap.BACKSPACE:
      case KeyMap.DELETE:
        setQuery((prev) => prev.slice(0, -1));
        // onClose();
        break;
      case KeyMap.ENTER:
        const item = filtered[activeIndex];
        if (item) {
          onSelect(item);
        }
        break;
      case KeyMap.ESC:
        onClose();
        break;
    }
  }

  function keydown(e) {
    if (e.keyCode === KeyMap.ENTER) {
      e.preventDefault();
    }
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowRight':
        e.preventDefault();
        break;
      case 'ArrowDown':
        e.preventDefault();
        highlightNext();
        break;
      case 'ArrowUp':
        e.preventDefault();
        highlightPrevious();
        break;
    }
    e.stopPropagation();
  }

  function highlightNext() {
    const count = suggestions.length;
    if (count > 0) {
      const i = activeIndex === count - 1 ? 0 : activeIndex + 1;
      setActiveIndex(i);
    }
  }

  function highlightPrevious() {
    const count = suggestions.length;
    if (count > 0) {
      const i = activeIndex === 0 ? count - 1 : activeIndex - 1;
      setActiveIndex(i);
    }
  }

  function bindEvents() {
    editor.on('keyup', keyup);
    editor.on('keydown', keydown);
  }

  function unbindEvents() {
    editor.off('keyup', keyup);
    editor.off('keydown', keydown);
  }

  const onSelect = (item) => {
    const rng = editor.selection.getRng();
    const start = rng.startContainer.textContent.lastIndexOf(
      defaultDelimiter,
      rng.startOffset,
    );
    const end = rng.startOffset;
    editor.selection.setRng(rng);
    rng.setStart(rng.startContainer, start);
    rng.setEnd(rng.startContainer, end);
    editor.selection.setRng(rng);
    const span = `<span contenteditable="false" class="mce-mention" data-mention-id="${item.value}">@${item.label}</span> `;
    editor.selection.setContent(span);
    editor.focus();
    // place cursor after the mention
    const newRng = editor.selection.getRng();
    newRng.setStart(newRng.endContainer, newRng.endOffset);
    newRng.setEnd(newRng.endContainer, newRng.endOffset);
    editor.selection.setRng(newRng);
    onClose();
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        position: 'absolute',
        top: `${rect.y + y + 20}px`,
        left: `${rect.x + x}px`,
        maxWidth: 400,
        maxHeight: 300,
        overflowY: 'auto',
        zIndex: 9999,
      }}
    >
      <Ul>
        {filtered.map((item, idx) => (
          <Li
            className={activeIndex === idx && 'Mui-selected'}
            key={item.value}
            onClick={() => onSelect(item)}
          >
            {item.label}
          </Li>
        ))}
      </Ul>
    </Paper>
  );
}

const mentionPlugin = (editor) => {
  let dropdown = null;
  let activeIndex = -1;
  const container = document.createElement('div');
  document.querySelector('#root').appendChild(container);

  function renderDropdown(editor, suggestions, initialQuery) {
    hideAutocomplete();
    if (suggestions.length > 0) {
      if (activeIndex === -1) {
        activeIndex = 0;
      }
      dropdown = document.createElement('div');
      const range = editor.selection.getRng();

      ReactDOM.render(
        <Dropdown
          editor={editor}
          suggestions={suggestions}
          onClose={hideAutocomplete}
          initialQuery={initialQuery}
        />,
        container,
      );
    }
  }

  function hideAutocomplete() {
    if (dropdown) {
      dropdown.remove();
      dropdown = null;
      activeIndex = -1;
      ReactDOM.unmountComponentAtNode(container);
    }
  }

  function showAutocomplete(editor, source, initialQuery) {}

  function handleKeyDown(e) {
    if (e.key === defaultDelimiter) {
      const mentionOptions = editor.getParam('mention_options', []);
      renderDropdown(editor, mentionOptions);
    } else if (dropdown) {
      switch (e.keyCode) {
        case KeyMap.ENTER:
          e.preventDefault();
          break;
        case KeyMap.SPACE:
          hideAutocomplete();
          break;
      }
    } else {
      // check if the user is typing has mention trigger.
      // The range should have trigger and no space after the trigger with one continuous string
      const rng = editor.selection.getRng();
      const node = rng.startContainer;
      const content = node.textContent;
      const cursorPosition = rng.startOffset;
      const mentionIndex = content.lastIndexOf(
        defaultDelimiter,
        cursorPosition - 1,
      );
      if (mentionIndex > -1) {
        const contentAfterMention = content.substring(mentionIndex + 1);
        if (contentAfterMention.indexOf(' ') === -1) {
          const mentionOptions = editor.getParam('mention_options', []);
          renderDropdown(editor, mentionOptions, contentAfterMention);
        } else {
          hideAutocomplete();
        }
      }
    }
  }

  editor.on('keydown', handleKeyDown);
};

export default mentionPlugin;
